import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Booking & Appointments',
      questions: [
        {
          question: 'How do I book a massage appointment in Sacramento?',
          answer: 'You can book your appointment online through our scheduling system at ayumisuzuki.com/book, email us at ayumisuzuki@yahoo.com, or text 415-678-9133. We offer convenient online booking available 24/7.'
        },
        {
          question: 'What are your hours and availability?',
          answer: 'We offer flexible scheduling Tuesday through Saturday with appointments available from 9:00 AM to 6:00 PM. Evening and weekend appointments are available based on demand. Book online to see real-time availability.'
        },
        {
          question: 'What is your cancellation policy?',
          answer: 'We require 24 hours notice for cancellations or rescheduling. This allows us to offer your time slot to another client. Late cancellations may be subject to a fee.'
        },
        {
          question: 'Do you offer same-day appointments?',
          answer: 'When available, same-day appointments can be booked online or by texting 415-678-9133. Availability varies, so we recommend booking in advance when possible.'
        }
      ]
    },
    {
      category: 'Services & Pricing',
      questions: [
        {
          question: 'What types of massage do you offer?',
          answer: 'We specialize in therapeutic massage, deep tissue massage, and Swedish massage. Each session is customized to your specific needs, whether you\'re seeking pain relief, stress management, or general wellness.'
        },
        {
          question: 'How much does a massage cost?',
          answer: 'Our pricing ranges from $55 for a 30-minute session to $200 for a 120-minute session. We offer: 30 minutes ($55), 45 minutes ($70), 60 minutes ($110), 90 minutes ($160), and 120 minutes ($200).'
        },
        {
          question: 'Which massage length should I choose?',
          answer: '30-45 minutes is ideal for targeted work on specific areas. 60 minutes provides a full-body massage with time for all major muscle groups. 90-120 minutes allows for comprehensive therapeutic work with extra attention to problem areas.'
        },
        {
          question: 'Do you offer package deals or memberships?',
          answer: 'Yes! Contact us to learn about our package options for regular clients. Packages offer savings on multiple sessions and help you maintain consistent wellness care.'
        }
      ]
    },
    {
      category: 'Location & Accessibility',
      questions: [
        {
          question: 'Where are you located in Sacramento?',
          answer: 'We are located at 2528 I Street in downtown Sacramento, in the heart of the Midtown district. Our location is easily accessible by car, public transit, bike, or on foot.'
        },
        {
          question: 'Is parking available?',
          answer: 'Street parking is available on I Street and surrounding blocks. There are also several paid parking lots nearby. The area is very walkable if you\'re coming from other downtown locations.'
        },
        {
          question: 'Are you accessible by public transportation?',
          answer: 'Yes! We\'re conveniently located near several RT bus lines and are within walking distance from the Capitol/16th Street light rail station. Downtown Sacramento\'s excellent transit access makes appointments easy.'
        }
      ]
    },
    {
      category: 'First Time Clients',
      questions: [
        {
          question: 'What should I expect during my first visit?',
          answer: 'Your first visit includes a brief consultation to discuss your health history, areas of concern, and goals. We\'ll explain the treatment approach and answer questions. The massage itself follows, with communication throughout to ensure your comfort.'
        },
        {
          question: 'What should I wear to my appointment?',
          answer: 'Wear comfortable clothing. You\'ll undress to your comfort level for the massage (most clients undress completely, but you\'re always properly draped). We provide privacy for changing and maintain professional standards throughout.'
        },
        {
          question: 'Should I arrive early for my first appointment?',
          answer: 'Please arrive 5-10 minutes early for your first appointment to complete intake forms. This ensures we have time for a thorough consultation and can start your massage on schedule.'
        },
        {
          question: 'What if I\'ve never had a massage before?',
          answer: 'First-time clients are welcome! We\'ll explain everything and ensure you feel comfortable throughout. Many first-timers start with a 60-minute Swedish massage to experience the benefits of massage therapy in a gentle, relaxing way.'
        }
      ]
    },
    {
      category: 'Health & Safety',
      questions: [
        {
          question: 'Is massage therapy safe?',
          answer: 'Yes, massage therapy is very safe when performed by a certified, licensed professional. We follow all California state regulations, maintain strict hygiene standards, and customize pressure to your comfort level.'
        },
        {
          question: 'Are you a licensed massage therapist?',
          answer: 'Yes, Ayumi Suzuki is a certified massage therapist licensed in California. She maintains continuing education requirements and adheres to all professional standards and ethics.'
        },
        {
          question: 'Do you have any contraindications?',
          answer: 'Certain health conditions require medical clearance before massage. Please inform us if you have: recent injuries, surgery, blood clots, skin infections, fever, or are pregnant. We\'ll discuss any concerns during your consultation.'
        },
        {
          question: 'What COVID-19 safety measures do you follow?',
          answer: 'We maintain enhanced cleaning protocols, sanitize all surfaces between clients, and follow current health guidelines. Our practice prioritizes your safety and comfort.'
        }
      ]
    },
    {
      category: 'Treatment & Results',
      questions: [
        {
          question: 'How often should I get a massage?',
          answer: 'For chronic pain or injury recovery, weekly or bi-weekly sessions provide best results. For stress management and maintenance, monthly sessions work well. For general wellness, quarterly sessions can be sufficient. We\'ll recommend a schedule based on your needs.'
        },
        {
          question: 'Will massage help with my specific condition?',
          answer: 'Massage therapy effectively treats many conditions including chronic pain, tension headaches, sports injuries, stress, poor circulation, and limited mobility. During your consultation, we\'ll discuss your specific concerns and create an appropriate treatment plan.'
        },
        {
          question: 'What should I do after my massage?',
          answer: 'Drink plenty of water to help flush toxins, avoid strenuous activity for a few hours, and allow your body to rest and integrate the work. You may feel relaxed or energized—both are normal. Benefits often increase over the 24-48 hours following treatment.'
        },
        {
          question: 'Will the massage hurt?',
          answer: 'Therapeutic and deep tissue massage may cause some discomfort in tight areas, but should never be painful. We maintain open communication about pressure levels and adjust to your comfort. Soreness similar to post-workout muscle fatigue may occur but typically resolves within 24 hours.'
        }
      ]
    },
    {
      category: 'Payment & Insurance',
      questions: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept cash, credit cards, and digital payment methods. Payment is due at the time of service. We also accept HSA and FSA cards for qualified expenses.'
        },
        {
          question: 'Do you accept insurance?',
          answer: 'While we don\'t bill insurance directly, we can provide detailed receipts for you to submit to your insurance company if you have coverage for massage therapy. Some plans cover massage when prescribed by a physician.'
        },
        {
          question: 'Can I use my HSA or FSA for massage?',
          answer: 'Yes! Massage therapy often qualifies as an eligible expense under Health Savings Accounts (HSA) and Flexible Spending Accounts (FSA). We provide documentation needed for reimbursement.'
        }
      ]
    },
    {
      category: 'About Ayumi Suzuki',
      questions: [
        {
          question: 'What are your qualifications and experience?',
          answer: 'Ayumi Suzuki is a certified massage therapist licensed in California, specializing in therapeutic massage, deep tissue, and Swedish massage. Her practice is rooted in a personal philosophy that healing touch transforms both body and mind.'
        },
        {
          question: 'What makes your practice unique?',
          answer: 'We offer personalized, focused, and intuitive bodywork with each session tailored to your individual needs. Our downtown Sacramento location provides convenient access, and our commitment to your wellness creates a supportive, healing environment.'
        },
        {
          question: 'Do you offer mobile or outcall massage?',
          answer: 'For wedding day services and special events, we offer mobile massage. Text 415-678-9133 to discuss your specific needs and availability.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 text-white/90 hover:text-white transition-colors duration-300 group mb-6"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-5xl md:text-6xl font-serif mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-teal-100">Everything you need to know about massage therapy in Sacramento</p>
        </div>
      </div>

      {/* FAQs */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {faqs.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-12">
            <h2 className="text-3xl font-serif text-gray-800 mb-6">{category.category}</h2>
            <div className="space-y-4">
              {category.questions.map((faq, questionIndex) => {
                const index = `${categoryIndex}-${questionIndex}`;
                const isOpen = openIndex === index;
                
                return (
                  <div
                    key={questionIndex}
                    className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg"
                  >
                    <button
                      onClick={() => toggleFAQ(categoryIndex, questionIndex)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 className="text-lg font-medium text-gray-800 pr-8">{faq.question}</h3>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                    </button>
                    
                    {isOpen && (
                      <div className="px-6 pb-6">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-serif mb-4">Still Have Questions?</h2>
          <p className="text-xl text-teal-100 mb-8">
            We're here to help! Contact us directly or schedule a consultation to discuss your specific needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book"
              className="inline-block bg-white text-teal-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-300"
            >
              Book Appointment
            </Link>
            <a
              href="mailto:ayumisuzuki@yahoo.com"
              className="inline-block bg-teal-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-900 transition-colors duration-300"
            >
              Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;